import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import clsx from "clsx"
import { useAuth } from "@/context/AuthContext"
import { createStarterGameProfile, getGuardianPaths } from "@/api/gameApi"
import { formatOnboardingApiError } from "@/api/gameApiErrors"
import {
  AcademicLeague,
  CardElement,
  GuardianPath,
} from "@/types/battle"
import { GuardianPathInfo } from "@/types/faction"
import {
  GUARDIAN_PATH_DEFAULTS,
  GUARDIAN_PATH_ORDER,
} from "@/constants/guardianPaths"
import { AFFILIATION_ELEMENTS } from "@/constants/affiliationInfo"
import { ELEMENT_NAMES } from "@/constants/guardians"
import { formatLeagueLabel } from "@/constants/leagueInfo"
import {
  STARTER_CARD_OPTIONS,
  STARTER_ELEMENT_LABELS,
  STARTER_STYLE_LABELS,
  StarterCardChoice,
} from "@/constants/starterCards"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import {
  CityRecord,
  ensureProfileCitySynced,
  loadGuardiansCities,
  resolveProfileCityId,
} from "@/features/passport/guardiansCity"
import { equipFirstStarterCard } from "@/features/passport/recoverGuardiansEntry"
import { getStoredGameReferrerCode } from "@/services/gameReferralStorage"
import GameScreenLayout from "@/components/GameScreenLayout"
import { hasSelectedLeague } from "@/utils/onboardingPassport"

type Step = "city" | "league" | "affiliation" | "path" | "cards" | "submit"

const STEPS: Step[] = ["city", "league", "affiliation", "path", "cards", "submit"]

export default function OnboardingWizard() {
  const navigate = useNavigate()
  const { profile, refreshProfile } = useAuth()
  const { passport, applyPassport, syncGameState } = useGuardiansPassport()

  const [step, setStep] = useState<Step>("city")
  const [cities, setCities] = useState<CityRecord[]>([])
  const [cityId, setCityId] = useState<number | null>(null)
  const [league, setLeague] = useState<AcademicLeague | null>(null)
  const [affiliation, setAffiliation] = useState<CardElement | null>(null)
  const [path, setPath] = useState<GuardianPath | null>(null)
  const [pathOptions, setPathOptions] = useState<GuardianPathInfo[]>(
    GUARDIAN_PATH_ORDER.map((p) => GUARDIAN_PATH_DEFAULTS[p])
  )
  const [nickname, setNickname] = useState("")
  const [pick1, setPick1] = useState<StarterCardChoice | null>(null)
  const [pick2, setPick2] = useState<StarterCardChoice | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    void loadGuardiansCities().then(setCities)
    const name = String(profile?.name ?? "").trim()
    if (name) setNickname(name)
    const pid = resolveProfileCityId(profile)
    if (pid != null) setCityId(pid)
    if (passport?.academicLeague) setLeague(passport.academicLeague)
    if (passport?.affiliationElement) setAffiliation(passport.affiliationElement)
    if (passport?.guardianPath) setPath(passport.guardianPath)
    if (hasSelectedLeague(passport ?? ({} as never))) {
      setStep("cards")
    }
  }, [profile, passport])

  useEffect(() => {
    void getGuardianPaths()
      .then((paths) => {
        if (paths?.length) setPathOptions(paths)
      })
      .catch(() => {})
  }, [])

  const stepIndex = STEPS.indexOf(step)

  const picksValid = useMemo(() => {
    if (!pick1 || !pick2) return false
    return !(
      pick1.element === pick2.element &&
      pick1.style === pick2.style
    )
  }, [pick1, pick2])

  async function nextFromCity() {
    setError(null)
    setLoading(true)
    try {
      const synced = await ensureProfileCitySynced({
        profile,
        appCity: null,
        cities,
        refreshProfile,
      })
      if (synced == null && cityId == null) {
        setError("Выберите город")
        return
      }
      if (cityId != null) {
        const { updateUserCity } = await import("@/services/profile")
        await updateUserCity(cityId)
        await refreshProfile()
      }
      setStep("league")
    } catch (e) {
      setError(formatOnboardingApiError(e, "Не удалось сохранить город"))
    } finally {
      setLoading(false)
    }
  }

  async function submitProfile() {
    if (!league || !affiliation || !path || !pick1 || !pick2) {
      setError("Заполните все шаги")
      return
    }
    setLoading(true)
    setError(null)
    try {
      const referrer = getStoredGameReferrerCode()
      const result = await createStarterGameProfile({
        league,
        affiliationElement: affiliation,
        guardianPath: path,
        firstPick: pick1,
        secondPick: pick2,
        nickname: nickname.trim() || undefined,
        ...(referrer ? { referrerPublicGameCode: referrer } : {}),
      })
      let nextPassport = result.passport
      const cards = result.cards ?? []
      const equipped = await equipFirstStarterCard(nextPassport, cards)
      nextPassport = equipped.passport
      applyPassport(nextPassport, true, cards)
      await syncGameState()
      navigate("/play", { replace: true })
    } catch (e) {
      setError(formatOnboardingApiError(e, "Не удалось создать профиль"))
    } finally {
      setLoading(false)
    }
  }

  function togglePick(choice: StarterCardChoice) {
    const key = `${choice.element}-${choice.style}`
    const p1k = pick1 ? `${pick1.element}-${pick1.style}` : null
    const p2k = pick2 ? `${pick2.element}-${pick2.style}` : null
    if (p1k === key) {
      setPick1(null)
      return
    }
    if (p2k === key) {
      setPick2(null)
      return
    }
    if (!pick1) {
      setPick1(choice)
      return
    }
    if (!pick2) {
      setPick2(choice)
      return
    }
    setPick2(choice)
  }

  return (
    <GameScreenLayout title="Создание хранителя" backTo="/login">
      <div className="mb-4 flex gap-1">
        {STEPS.map((s, i) => (
          <div
            key={s}
            className={clsx(
              "h-1 flex-1 rounded-full",
              i <= stepIndex ? "bg-guardians-accent" : "bg-white/10"
            )}
          />
        ))}
      </div>

      {step === "city" && (
        <section className="space-y-3">
          <p className="text-sm text-guardians-muted">Город игры (как в DAKSBY)</p>
          <select
            className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm"
            value={cityId ?? ""}
            onChange={(e) => setCityId(Number(e.target.value) || null)}
          >
            <option value="">Выберите город</option>
            {cities.map((c) => (
              <option key={c.city_id} value={c.city_id ?? ""}>
                {c.title}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={loading}
            onClick={() => void nextFromCity()}
            className="w-full rounded-xl bg-guardians-accent py-3 text-sm font-medium text-guardians-bg"
          >
            Далее
          </button>
        </section>
      )}

      {step === "league" && (
        <section className="space-y-3">
          <p className="text-sm text-guardians-muted">Лига стихий</p>
          {(["TITAN", "ATLANT"] as AcademicLeague[]).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLeague(l)}
              className={clsx(
                "w-full rounded-xl border px-4 py-3 text-left text-sm",
                league === l
                  ? "border-guardians-accent bg-guardians-accent/10"
                  : "border-white/10"
              )}
            >
              {formatLeagueLabel(l)}
            </button>
          ))}
          <NavButtons
            onBack={() => setStep("city")}
            onNext={() => league && setStep("affiliation")}
            nextDisabled={!league}
          />
        </section>
      )}

      {step === "affiliation" && (
        <section className="space-y-3">
          <p className="text-sm text-guardians-muted">Стихия хранителя</p>
          <div className="grid grid-cols-2 gap-2">
            {AFFILIATION_ELEMENTS.map((el) => (
              <button
                key={el}
                type="button"
                onClick={() => setAffiliation(el)}
                className={clsx(
                  "rounded-xl border px-3 py-2 text-sm",
                  affiliation === el
                    ? "border-guardians-accent bg-guardians-accent/10"
                    : "border-white/10"
                )}
              >
                {ELEMENT_NAMES[el]}
              </button>
            ))}
          </div>
          <NavButtons
            onBack={() => setStep("league")}
            onNext={() => affiliation && setStep("path")}
            nextDisabled={!affiliation}
          />
        </section>
      )}

      {step === "path" && (
        <section className="space-y-2">
          <p className="text-sm text-guardians-muted">Путь хранителя</p>
          {pathOptions.map((opt) => (
            <button
              key={opt.path}
              type="button"
              onClick={() => setPath(opt.path)}
              className={clsx(
                "w-full rounded-xl border px-3 py-2 text-left text-sm",
                path === opt.path
                  ? "border-guardians-accent bg-guardians-accent/10"
                  : "border-white/10"
              )}
            >
              <div className="font-medium">{opt.title}</div>
              <div className="text-xs text-guardians-muted">{opt.description}</div>
            </button>
          ))}
          <NavButtons
            onBack={() => setStep("affiliation")}
            onNext={() => path && setStep("cards")}
            nextDisabled={!path}
          />
        </section>
      )}

      {step === "cards" && (
        <section className="space-y-3">
          <label className="block text-sm text-guardians-muted">
            Ник (необязательно)
            <input
              className="mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
            />
          </label>
          <p className="text-sm text-guardians-muted">
            Выберите 2 стартовые карты ({pick1 ? 1 : 0}/2)
          </p>
          <div className="grid max-h-64 grid-cols-2 gap-2 overflow-y-auto">
            {STARTER_CARD_OPTIONS.map((opt) => {
              const key = `${opt.element}-${opt.style}`
              const selected =
                (pick1 && `${pick1.element}-${pick1.style}` === key) ||
                (pick2 && `${pick2.element}-${pick2.style}` === key)
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => togglePick(opt)}
                  className={clsx(
                    "rounded-lg border px-2 py-2 text-xs",
                    selected
                      ? "border-guardians-accent bg-guardians-accent/15"
                      : "border-white/10"
                  )}
                >
                  {STARTER_ELEMENT_LABELS[opt.element]}
                  <br />
                  {STARTER_STYLE_LABELS[opt.style]}
                </button>
              )
            })}
          </div>
          <NavButtons
            onBack={() => setStep("path")}
            onNext={() => picksValid && setStep("submit")}
            nextDisabled={!picksValid}
          />
        </section>
      )}

      {step === "submit" && (
        <section className="space-y-3 text-sm">
          <p>Лига: {league ? formatLeagueLabel(league) : "—"}</p>
          <p>Стихия: {affiliation ? ELEMENT_NAMES[affiliation] : "—"}</p>
          <p>Путь: {path ? GUARDIAN_PATH_DEFAULTS[path].title : "—"}</p>
          <button
            type="button"
            disabled={loading}
            onClick={() => void submitProfile()}
            className="w-full rounded-xl bg-guardians-accent py-3 font-medium text-guardians-bg disabled:opacity-60"
          >
            {loading ? "Создание…" : "Начать игру"}
          </button>
          <button
            type="button"
            className="w-full text-sm text-guardians-muted"
            onClick={() => setStep("cards")}
          >
            ← Изменить карты
          </button>
        </section>
      )}

      {error ? <p className="mt-4 text-sm text-red-400">{error}</p> : null}
    </GameScreenLayout>
  )
}

function NavButtons({
  onBack,
  onNext,
  nextDisabled,
}: {
  onBack: () => void
  onNext: () => void
  nextDisabled?: boolean
}) {
  return (
    <div className="flex gap-2 pt-2">
      <button
        type="button"
        onClick={onBack}
        className="flex-1 rounded-xl border border-white/15 py-2 text-sm"
      >
        Назад
      </button>
      <button
        type="button"
        disabled={nextDisabled}
        onClick={onNext}
        className="flex-1 rounded-xl bg-guardians-accent py-2 text-sm font-medium text-guardians-bg disabled:opacity-50"
      >
        Далее
      </button>
    </div>
  )
}
